import * as discoverRoutines from './discoverRoutines';
import * as isBase64 from 'is-base64';
import * as paths from '../../Providers/paths';
import * as prettyBytes from 'pretty-bytes';
import * as rootSelectors from '../../selectors/root';
import * as toastRoutines from '../../sagas/routines/toastRoutines';
import * as ui_actions from '../../redux/ui/actions';
import { all, call, cancel, fork, put, select, take, takeEvery } from 'redux-saga/effects';
import { deleteFileFromStorageSaga } from '../../sagas/filesSaga';
import { extractFileTypeFromBase64 } from '../../helpers/fileTypeRegex';
import { isEmpty } from '../../helpers/isEmpty';
import { makeActiveDiscover, makeNextDiscoverIndexNumber } from './discoverSelectors';
import { rsf } from '../../globals/firebase';
import {
    selectActiveDiscoverId,
    selectActiveDiscoverLocation,
    selectActiveDiscoverParentList,
} from '../../redux/ui/uiSelectors';
import { sendFileSaga, sendStringSaga } from '../../sagas/filesSaga';
import { transformArrayToObject } from '../../helpers/firebaseTransformations';
import _ from 'lodash';
import config from '../../globals/config'

export function* createDiscoverSaga(action) {
    const active_event_id = yield select(rootSelectors.selectActiveEventId);
    const nextIndexNumber = yield select(makeNextDiscoverIndexNumber);
    const { content, location } = action.payload;
    try {
        let res = yield call(rsf.database.create, `${paths.moduleConfig(active_event_id, 'discover', location)}`, {
            content,
            index: nextIndexNumber,
            isNameHidden: false,
        });
        yield put(discoverRoutines.createDiscover.success(res));
        yield put(ui_actions.setActiveDiscover(res, 'top'));
        yield put(toastRoutines.setToastSuccess('Discover successfully added!'));
    } catch (error) {
        yield put(toastRoutines.setToastError('Error adding discover!'));
        yield put(discoverRoutines.createDiscover.failure(error));
    }
}

export function* moveDiscoverNodeSaga(action) {
    const { other, top } = action.payload;
    yield put(discoverRoutines.stopSyncingDiscover.trigger());
    yield call(updateDiscoverBranchSaga, {
        payload: {
            location: 'top',
            nodes: top,
        },
    });
    yield call(updateDiscoverBranchSaga, {
        payload: {
            location: 'other',
            nodes: other,
        },
    });
    yield put(discoverRoutines.syncDiscover.trigger());
}

export function* updateDiscoverBranchSaga(action) {
    const active_event_id = yield select(rootSelectors.selectActiveEventId);
    let { location, nodes } = action.payload;
    try {
        yield call(rsf.database.update, `${paths.moduleConfig(active_event_id, 'discover', location)}`, nodes);
        yield put(discoverRoutines.updateDiscoverBranch.success());
    } catch (error) {
        yield put(toastRoutines.setToastError('Error updating Discover!'));
        yield put(discoverRoutines.updateDiscoverBranch.failure(error));
    }
}
export function* updateDiscoverTreeBackgroundSaga(action) {
    const active_event_id = yield select(rootSelectors.selectActiveEventId);
    try {
        const filePath = paths.storage_discover_global_background(active_event_id);
        const downloadableZoomImage = yield call(sendStringSaga, { filePath, imageURL: action.payload });
        yield call(
            rsf.database.update,
            `${paths.moduleConfig(active_event_id, 'discover')}/topCommonBackgroundImageURL/`,
            downloadableZoomImage,
        );
        yield put(discoverRoutines(toastRoutines.setToastSuccess('Discover background added!')));
        yield put(discoverRoutines.updateDiscoverTreeBackground.success());
    } catch (error) {
        yield put(toastRoutines.setToastError('Error updating Discover background!'));
        yield put(discoverRoutines.updateDiscoverTreeBackground.failure(error));
    }
}

//Combine abstract the following two methods into a more generic, re-usable one
function* uploadDocumentSaga(action) {
    const url = yield call(sendFileSaga, { filePath: action.docStoragePath, file: action.doc });
    //Return document object
    return {
        name: action.doc.name,
        size: prettyBytes(action.doc.size),
        url,
    };
}

function* uploadAdSaga(action) {
    const url = yield call(sendFileSaga, { filePath: action.adStoragePath, file: action.ad });
    //Return document object
    return {
        name: action.ad.name,
        size: prettyBytes(action.ad.size),
        url,
    };
}

//Returns appropriate path based on discover node status as parent or child
const obtainDiscoverPathValue = ({
    active_discover_id,
    active_discover_location,
    active_discover_parent_list,
    active_event_id,
    path,
}) => {
    let targetPath; //Used to assign "top" or "other for discover location

    //Identifies discover card location
    if (path) {
        targetPath = path;
    } else {
        //We are going to use the store to construct the path
        if (active_discover_parent_list) {
            //If there is an active parent list
            targetPath = `${active_discover_location}/${active_discover_parent_list}/${active_discover_id}`;
        } else {
            targetPath = `${active_discover_location}/${active_discover_id}`;
        }
    }
    return `${paths.moduleConfig(active_event_id, 'discover')}/${targetPath}/`;
};

export function* updateDiscoverSaga(action) {
    //Instantiate useful variables for this saga
    const active_event_id = yield select(rootSelectors.selectActiveEventId);
    const active_discover_id = yield select(selectActiveDiscoverId);
    const active_discover_location = yield select(selectActiveDiscoverLocation);
    const active_discover_parent_list = yield select(selectActiveDiscoverParentList);
    const activeDiscover = yield select(makeActiveDiscover);

    //Destructure payload properties
    let { cardBackground, cardImageURL, content, headerName, index, isAd, isNameHidden, isSmallSize, name, path } = action.payload;
    //Ensures falsey values are stored as null, so Firebase removes key/value
    isAd = isAd ? isAd : null;
    isNameHidden = isNameHidden ? isNameHidden : null;
    isSmallSize = isSmallSize ? isSmallSize : null;
    headerName = headerName ? headerName : null;

    /***
     *
     * Check to see if documents are being uploaded with the same filename as one that exists
     */
    yield put(discoverRoutines.setDiscoverCardStatus('saving'));
    if (_.get(content, 'documents.toUpload')) {
        yield call(validateDocumentFileNames, content)
    }

    /***
     *
     * Check to see if ads are being uploaded with the same filename as one that exists
     */
    if (_.get(content, 'ads.toUpload')) {
        yield call(validateAdFileNames, content)
    }

    //Notify that we are uploading
    yield put(
        toastRoutines.triggerToast({
            content: 'Updating discover card...',
            options: { type: 'info', autoClose: false },
        }),
    );
    yield put(discoverRoutines.setDiscoverCardStatus('saving'));

    //Obtain path of node (can vary if parent/top or child/other)
    const pathOfNode = obtainDiscoverPathValue({
        active_event_id,
        active_discover_id,
        active_discover_location,
        active_discover_parent_list,
        path,
    });

    //Handle different background image selections
    let shouldUseCommonBackgroundImage = null;
    let removeDiscoverCardImageFromStorage = false;
    switch (cardBackground) {
        case 'global':
            shouldUseCommonBackgroundImage = true;
            break;
        case 'custom':
            //Only upload the file if it is base-64
            if (isBase64(cardImageURL, { mime: true })) {
                if (activeDiscover.cardImageURL)
                    yield call(
                        deleteFileFromStorageSaga,
                        activeDiscover.cardImageURL,
                        paths.storageDiscover(active_event_id, active_discover_id),
                    );
                const fileType = extractFileTypeFromBase64(cardImageURL);
                const cardBackgroundFilepath =
                    paths.storageDiscover(active_event_id, active_discover_id) + `background.${fileType}`;
                const downloadableCardBackground = yield call(sendStringSaga, {
                    filePath: cardBackgroundFilepath,
                    imageURL: cardImageURL,
                });
                cardImageURL = downloadableCardBackground;
            }
            break;
        case 'none':
            if (activeDiscover.cardImageURL)
                yield call(
                    deleteFileFromStorageSaga,
                    activeDiscover.cardImageURL,
                    paths.storageDiscover(active_event_id, active_discover_id),
                );

            //Let's flag that we need to remove image from storage
            if (!isEmpty(cardImageURL)) removeDiscoverCardImageFromStorage = true;
            cardImageURL = null;
            break;
        default:
            break;
    }

    switch (content.type) {
        case 'canvas':
            const { sitePageId } = content;
            content.url = `${config.REACT_APP_HOST_PRIMARY}/app/pages/${sitePageId}`;
            break;
        //Takes base64 encoded image, stores in Firebase Storage and replaces content.imageURL with the storage location
        case 'zoomImage':
        case 'info':
            //Only upload the file if it is base-64
            if (
                content.imageURL === undefined ||
                (content.imageURL !== null && isBase64(content.imageURL, { mime: true }))
            ) {
                //If new image as added, or no image is being uploaded, we want to remove existing
                if (activeDiscover.content.imageURL)
                    yield call(
                        deleteFileFromStorageSaga,
                        activeDiscover.content.imageURL,
                        paths.storageDiscover(active_event_id, active_discover_id),
                    );
                if (content.imageURL === undefined) break;
                const fileType = extractFileTypeFromBase64(content.imageURL);
                const filePath = paths.storageDiscover(active_event_id, active_discover_id) + `content.${fileType}`;
                const downloadableImage = yield call(sendStringSaga, { filePath, imageURL: content.imageURL });
                content.imageURL = downloadableImage;
            }

            break;
        default:

            //Triggers file removal when either zoomImage or info card is chosen
            if (activeDiscover.content.imageURL)
                yield call(
                    deleteFileFromStorageSaga,
                    activeDiscover.content.imageURL,
                    paths.storageDiscover(active_event_id, active_discover_id),
                );

            break;
    }

    let { documents, ads,  ...nextNodeContent } = _.cloneDeep(content);
    //If uploaded documents exist, let's bind this to the node to retain them in the db
    if (_.get(documents, 'uploaded')) {
        nextNodeContent = { ...nextNodeContent, documents: transformArrayToObject(_.get(documents, 'uploaded'), 'id') };
    }

    if (_.get(ads, 'uploaded')) {
        nextNodeContent = { ...nextNodeContent, ads: transformArrayToObject(_.get(ads, 'uploaded'), 'id') };
    }
    const nextNode = {
        content: nextNodeContent, //Uses content payload without the documents value, that will come later
        name,
        index,
        isAd,
        isNameHidden,
        isSmallSize,
        headerName,
        cardImageURL,
        shouldUseCommonBackgroundImage,
    };

    try {
        yield call(rsf.database.patch, pathOfNode, nextNode);

        //If we have documents to add to the content node we do that now
        if (_.get(content, 'documents.toUpload') && content.documents.toUpload.length > 0) {
            const docStoragePath = paths.storageDiscover(active_event_id, active_discover_id) + `/Documents/`;
            yield call(handleDocumentsUpload, docStoragePath, pathOfNode, content)
        }
        //If we have ads to add to the content node we do that now
        if (_.get(content, 'ads.toUpload') && content.ads.toUpload.length > 0) {
            const adsStoragePath = paths.storageDiscover(active_event_id, active_discover_id) + `/Ads/`;
            yield call(handleAdsUpload, adsStoragePath, pathOfNode,  content.ads.toUpload)
        }

        yield put(toastRoutines.dismissToast());

        //Useful to hide/show document uploader
        yield put(discoverRoutines.setDiscoverCardStatus('saved'));
        yield put(toastRoutines.setToastSuccess('Discover successfully updated!'));
        yield put(discoverRoutines.updateDiscover.success());
    } catch (error) {
        yield put(toastRoutines.dismissToast());
        yield put(toastRoutines.setToastError('Error updating Discover!'));
        yield put(discoverRoutines.updateDiscover.failure(error));
    }
}

function* handleDocumentsUpload(storagePath, pathOfNode, data){
        let documentObjects;
        //Saves to storage and formats our objects to store in firebase
        documentObjects = yield all(
            data.documents.toUpload.map((doc, idx) => {
                return call(uploadDocumentSaga, {
                    doc,
                    docStoragePath: `${storagePath}${doc.name}`,
                    pathOfNode,
                });
            }),
        );
        // Maps through the objects created above
        yield all(
            documentObjects.map(obj => {
                return call(rsf.database.create, pathOfNode + '/content/documents/', obj);
            }),
        );
}

function* handleAdsUpload(storagePath, pathOfNode, data){
    let adObjects;

    //Saves to storage and formats our objects to store in firebase
    adObjects = yield all(
        data.map((ad, idx) => {
            return call(uploadAdSaga, {
                ad,
                adStoragePath: `${storagePath}${ad.name}`,
                pathOfNode,
            });
        }),
    );

    //Set cardImageURL with a default
    if(adObjects.length > 0) yield call(rsf.database.patch, pathOfNode, { activeAdIdx:0, cardImageURL: adObjects[0]['url'] });

    // Maps through the objects created above
    yield all(
        adObjects.map(obj => {
            return call(rsf.database.create, pathOfNode + '/content/ads/', obj);
        }),
    );

}
function* validateDocumentFileNames(content){
    //intersection contains the collection of filenames that we are trying to upload that are already uploaded
    const intersection = _.intersectionBy(
        _.get(content, 'documents.uploaded'),
        _.get(content, 'documents.toUpload'),
        (a, b) => {
            return a.name;
        },
    ).map(obj => obj.name);
    yield all(
        intersection.map(filename => {
            return put(
                toastRoutines.triggerToast({
                    content: `Document with filename ${filename} already exists!`,
                    options: { type: 'error', autoClose: true },
                }),
            );
        }),
    );
    yield put(discoverRoutines.setDiscoverCardStatus('open'));

    if (intersection.length > 0) {
        yield put(discoverRoutines.resetDocumentsToUpload(true));
        yield put(discoverRoutines.resetDocumentsToUpload(false));

        return false;
    }
}

function* validateAdFileNames(content){
    //intersection contains the collection of filenames that we are trying to upload that are already uploaded
    const intersection = _.intersectionBy(
        _.get(content, 'ads.uploaded'),
        _.get(content, 'ads.toUpload'),
        (a, b) => {
            return a.name;
        },
    ).map(obj => obj.name);
    yield all(
        intersection.map(filename => {
            return put(
                toastRoutines.triggerToast({
                    content: `Ad with filename ${filename} already exists!`,
                    options: { type: 'error', autoClose: true },
                }),
            );
        }),
    );
    yield put(discoverRoutines.setDiscoverCardStatus('open'));

    if (intersection.length > 0) {
        yield put(discoverRoutines.resetAdsToUpload(true));
        yield put(discoverRoutines.resetAdsToUpload(false));

        return false;
    }
}


//TODO refactor - consolidate the following two methods into one
export function* removeDocumentFromDiscoverSaga(action) {
    yield put(
        toastRoutines.triggerToast({
            content: `Removing ${action.payload.name}`,
            options: { type: 'info', autoClose: false },
        }),
    );
    const active_event_id = yield select(rootSelectors.selectActiveEventId);
    const active_discover_id = yield select(selectActiveDiscoverId);
    const active_discover_location = yield select(selectActiveDiscoverLocation);
    const active_discover_parent_list = yield select(selectActiveDiscoverParentList);
    const pathOfNode = obtainDiscoverPathValue({
        active_event_id,
        active_discover_id,
        active_discover_location,
        active_discover_parent_list,
        // path,
    });
    try {
        yield call(rsf.database.delete, pathOfNode + '/content/documents/' + action.payload.id);
        yield call(
            deleteFileFromStorageSaga,
            action.payload.url,
            paths.storageDiscover(active_event_id, active_discover_id) + '/Documents',
        );
    } catch (error) {
        yield put(
            toastRoutines.triggerToast({
                content: `Error removing ${action.payload.name}`,
                options: { type: 'error', autoClose: true },
            }),
        );
    }
    yield put(toastRoutines.dismissToast());
}


export function* removeAdFromDiscoverSaga(action) {
    yield put(
        toastRoutines.triggerToast({
            content: `Removing ${action.payload.name}`,
            options: { type: 'info', autoClose: false },
        }),
    );
    const active_event_id = yield select(rootSelectors.selectActiveEventId);
    const active_discover_id = yield select(selectActiveDiscoverId);
    const active_discover_location = yield select(selectActiveDiscoverLocation);
    const active_discover_parent_list = yield select(selectActiveDiscoverParentList);
    const pathOfNode = obtainDiscoverPathValue({
        active_event_id,
        active_discover_id,
        active_discover_location,
        active_discover_parent_list,
        // path,
    });
    try {
        yield call(rsf.database.delete, pathOfNode + '/content/ads/' + action.payload.id);
        yield call(
            deleteFileFromStorageSaga,
            action.payload.url,
            paths.storageDiscover(active_event_id, active_discover_id) + '/Ads',
        );
    } catch (error) {
        yield put(
            toastRoutines.triggerToast({
                content: `Error removing ${action.payload.name}`,
                options: { type: 'error', autoClose: true },
            }),
        );
    }
    yield put(toastRoutines.dismissToast());
}

export function* removeDiscoverSaga(action) {
    yield put(discoverRoutines.clearDiscoverData());

    const active_event_id = yield select(rootSelectors.selectActiveEventId);
    const active_discover_id = yield select(selectActiveDiscoverId);
    const active_discover_location = yield select(selectActiveDiscoverLocation);
    const active_discover_parent_list = yield select(selectActiveDiscoverParentList);

    let targetPath;
    if (action.payload && action.payload.path) {
        targetPath = action.payload.path;
    } else {
        if (active_discover_parent_list) {
            targetPath = `${active_discover_location}/${active_discover_parent_list}/${active_discover_id}`;
        } else {
            targetPath = `${active_discover_location}/${active_discover_id}`;
        }
    }
    try {
        yield call(rsf.database.delete, paths.moduleConfig(active_event_id, 'discover', targetPath));
        yield put(ui_actions.setActiveDiscover(null, null, null));
        yield put(toastRoutines.setToastSuccess('Discover successfully removed!'));
        yield put(discoverRoutines.removeDiscover.success());
    } catch (error) {
        yield put(toastRoutines.setToastError('Error removing discover!'));
        yield put(discoverRoutines.removeDiscover.failure(error));
    }
}

function* syncDiscoverSaga() {
    const active_event_id = yield select(rootSelectors.selectActiveEventId);
    yield fork(rsf.database.sync, paths.moduleConfig(active_event_id, 'discover'), {
        successActionCreator: discoverRoutines.syncDiscover.success,
        failureActionCreator: discoverRoutines.syncDiscover.failure,
    });
}
export function* reorderDiscoverSaga(action) {
    const active_event_id = yield select(rootSelectors.selectActiveEventId);
    let { id, index, location } = action.payload;
    try {
        yield call(rsf.database.patch, `${paths.moduleConfig(active_event_id, 'discover', location, id)}`, { index });
        put(discoverRoutines.reorderDiscover.success());
    } catch (error) {
        put(discoverRoutines.reorderDiscover.failure(error));
    }
}

export function* resetDiscoverSync() {
    yield put(discoverRoutines.stopSyncingDiscover.trigger());
    yield put(discoverRoutines.syncDiscover.trigger());
}

export default function* rootSaga() {
    yield all([
        takeEvery(discoverRoutines.updateDiscoverTreeBackground.TRIGGER, updateDiscoverTreeBackgroundSaga),
        takeEvery(discoverRoutines.createDiscover.TRIGGER, createDiscoverSaga),
        takeEvery(discoverRoutines.updateDiscover.TRIGGER, updateDiscoverSaga),
        takeEvery(discoverRoutines.reorderDiscover.TRIGGER, reorderDiscoverSaga),
        takeEvery(discoverRoutines.removeDiscover.TRIGGER, removeDiscoverSaga),
        takeEvery(discoverRoutines.moveDiscoverNode.TRIGGER, moveDiscoverNodeSaga),
        takeEvery(discoverRoutines.updateDiscoverBranch.TRIGGER, updateDiscoverBranchSaga),
        takeEvery(discoverRoutines.removeDocumentFromDiscover.TRIGGER, removeDocumentFromDiscoverSaga),
        takeEvery(discoverRoutines.removeAdFromDiscover.TRIGGER, removeAdFromDiscoverSaga),

    ]);
    while (yield take(discoverRoutines.syncDiscover.TRIGGER)) {
        const bgDiscoverSync = yield fork(syncDiscoverSaga);
        yield take(discoverRoutines.stopSyncingDiscover.TRIGGER);
        yield cancel(bgDiscoverSync);
    }
}
